/**
 * SettingsPanel — headless settings sheet.
 *
 * Headless: no `browser.*`, no `chrome.*`, no `theme.ts`, no storage
 * hooks. All data arrives as props; theme + notification toggles +
 * disconnect are wired by the host through callbacks; all i18n is
 * supplied via the `labels` bag. Mirrors the conventions established
 * by `SidePanelNotificationList` / `NotificationListItem`.
 *
 * Sections:
 *   - Appearance — always rendered (theme picker via `TabGroup`).
 *   - Notifications — rendered when at least one of `osNotifications`
 *     or `callNotifications` is provided. Hidden in the web shell
 *     today (the SW doesn't yet gate on either preference).
 *   - Privacy — rendered when `privacy` is provided. Currently a
 *     single toggle (anonymous usage analytics) + description
 *     caption. Bundles its own `labels` sub-bag (rather than adding
 *     required keys to the shared `SettingsPanelLabels`) so hosts
 *     that don't wire analytics yet keep compiling unchanged.
 *   - Connection — rendered when `connection` is provided. The
 *     two-step confirm flow (safety net against accidental taps) is
 *     local UI state owned by this component; the actual disconnect
 *     IPC is the host's `onDisconnect()` returning a Promise. If it
 *     throws, the confirm dialog resets so the user lands back on
 *     the primary button rather than a stuck "loading" Yes button.
 *   - Send feedback — rendered when both `onSendFeedback` and
 *     `labels.sendFeedback` are provided. A single button the host
 *     wires to open its feedback modal.
 *   - About — version + encryption + optional support email footer.
 *     `version` and `supportEmail` are independent — pass either,
 *     both, or neither.
 *
 * State that stays inside the component:
 *   - `confirming` (two-step disconnect dialog)
 *   - `disconnecting` (loading state on the Yes button)
 *
 * The host owns: theme persistence, theme application
 * (document.documentElement attribute), notification toggle
 * persistence, the actual disconnect IPC, and all i18n.
 */

import type { ReflectoStorage } from "@reflecto/client-core";
import { Check, ChevronLeft, Contrast01, LogOut04, Moon01, Sun } from "@untitledui/icons";
import { type ReactNode, useCallback, useId, useState } from "react";
import { splitOnEmailSentinel } from "../../utils/emailSentinel";
import { Button } from "../Button";
import { Chip } from "../Chip";
import { IconButton } from "../IconButton";
import { Link } from "../Link";
import { Tab, TabGroup } from "../TabGroup";
import { Text } from "../Text";
import { Toggle } from "../Toggle";
import styles from "./SettingsPanel.module.css";

/**
 * Derived from the canonical storage schema in `@reflecto/client-core`
 * so adding a new theme value (e.g. "high-contrast") forces a compile
 * error in every consumer at once — both the extension wrapper and the
 * web wrapper share this type, so an independent local union here
 * would silently drift apart from the schema and from each other.
 *
 * Type-only import — Rollup erases it at build time, so this doesn't
 * pull `@reflecto/client-core`'s runtime into the client-ui bundle
 * (matches the existing pattern for `ConversationThread` /
 * `DecryptedNotification` imports across this package).
 */
export type ThemeValue = ReflectoStorage["theme"];

/**
 * i18n bag for every visible string in the panel.
 *
 * `needHelp` is optional — when present alongside `supportEmail`, the
 * About footer renders the copy with the email anchor inlined at the
 * `__EMAIL__` sentinel. When absent (or `supportEmail` is missing),
 * the footer is omitted.
 */
export interface SettingsPanelLabels {
  back: string;
  settings: string;
  appearance: string;
  theme: string;
  themeAuto: string;
  themeLight: string;
  themeDark: string;
  notificationsSection: string;
  osNotifications: string;
  callNotifications: string;
  connection: string;
  status: string;
  statusConnected: string;
  statusReconnecting: string;
  statusDisconnected: string;
  disconnectFromPhone: string;
  disconnectConfirm: string;
  yesDisconnect: string;
  cancel: string;
  aboutAndSupport: string;
  version: string;
  encryption: string;
  encryptionValue: string;
  /** Optional. Should contain `__EMAIL__` sentinel — replaced with the supportEmail anchor inline. */
  needHelp?: string;
  /** Optional — label for the "Send feedback" entry. Renders only when `onSendFeedback` is also provided. */
  sendFeedback?: string;
}

/**
 * Toggle config — bundles the controlled value with its onChange.
 * Mirrors the IconButton pattern of grouping related props so the
 * SettingsPanel prop list stays readable as more sections are added.
 */
export interface SettingsPanelToggle {
  value: boolean;
  onChange(next: boolean): void;
}

/**
 * Connection section config. `paired` gates whether the disconnect
 * button renders at all; `connected` drives the status chip
 * (Connected / Reconnecting / Disconnected). `onDisconnect` is async
 * — the headless component manages the disconnecting/confirming
 * state machine and resets on failure (caught Promise rejection).
 */
export interface SettingsPanelConnectionConfig {
  paired: boolean;
  connected: boolean;
  /** Async — the wrapper handles the IPC / service call. Errors caught + reset by the headless component. */
  onDisconnect(): Promise<void>;
}

/**
 * i18n for the Privacy section. Bundled with `SettingsPanelPrivacyConfig`
 * (rather than folded into the shared `SettingsPanelLabels`) so these keys
 * are only ever required when a host actually wires the section — adding
 * them to the always-required `SettingsPanelLabels` would force every
 * existing consumer (both apps' label bags, every test fixture, every
 * story) to grow new required fields just to keep compiling.
 */
export interface SettingsPanelPrivacyLabels {
  /** Section heading, e.g. "Privacy". */
  section: string;
  /** Toggle label, e.g. "Anonymous usage statistics". Checked = participating. */
  analyticsToggle: string;
  /** Caption rendered under the toggle explaining what is/isn't captured. */
  analyticsToggleDescription: string;
}

/**
 * Privacy section config. `analyticsEnabled.value` is the PARTICIPATING
 * state (checked = collecting anonymous analytics), i.e. the inverse of
 * the host's persisted `analyticsOptOut` flag — the headless component
 * never sees the storage key name, only the boolean the switch should
 * show. `labels` is required here (not on the shared bag) — see the
 * type doc above.
 */
export interface SettingsPanelPrivacyConfig {
  analyticsEnabled: SettingsPanelToggle;
  labels: SettingsPanelPrivacyLabels;
}

/**
 * Slot config for a host-supplied section. The headless SettingsPanel
 * stays decoupled from feature-specific panels (Devices, Hidden Apps,
 * DND, …) by exposing the multi-device V1 sections as `extraSections`
 * — the host renders each section's body via `content` and the panel
 * supplies the matching SectionHeader chrome + divider so the new
 * section visually slots into the existing rhythm.
 *
 * Sections are rendered in array order, AFTER the built-in
 * Notifications and Connection sections and BEFORE the About footer.
 * `id` exists purely as a stable React key.
 */
export interface SettingsPanelExtraSection {
  /** Stable React key for the section. */
  id: string;
  /** Localized section heading shown above `content`. Omit to let `content` supply its own heading. */
  title?: string;
  /** Section body — rendered as-is inside the section wrapper. */
  content: ReactNode;
}

export interface SettingsPanelLanguageOption {
  /** Catalog registry tag ("de", "pt-BR", …). */
  tag: string;
  /** The language's self-name ("Deutsch", "日本語") — never translated. */
  endonym: string;
}

/**
 * Language-picker config. Optional — hosts that don't wire a language
 * setting keep compiling unchanged (the Privacy-section precedent).
 * Rendered as a row in the Appearance section, below the theme picker.
 */
export interface SettingsPanelLanguageConfig {
  /** Current override tag, or null when following the system language. */
  value: string | null;
  /** Selectable locales, endonym-labeled; host derives from its catalog registry. */
  options: SettingsPanelLanguageOption[];
  /** `null` = the user chose "System default". */
  onChange(tag: string | null): void;
  labels: {
    /** Row label, e.g. "Language". */
    language: string;
    /** First option — follow the system language. */
    systemDefault: string;
  };
}

export interface SettingsPanelProps {
  // ── Theme — always required ──────────────────────────────────────
  theme: ThemeValue;
  onThemeChange(theme: ThemeValue): void;

  // ── Notifications — pass undefined to hide entire section ────────
  osNotifications?: SettingsPanelToggle;
  callNotifications?: SettingsPanelToggle;

  // ── Language — pass undefined to hide the picker row ─────────────
  language?: SettingsPanelLanguageConfig;

  // ── Privacy — pass undefined to hide entire section ───────────────
  privacy?: SettingsPanelPrivacyConfig;

  // ── Connection — pass undefined to hide entire section ───────────
  connection?: SettingsPanelConnectionConfig;

  // ── Extra sections (e.g. DND) ────────────────────────────────────
  /**
   * Host-supplied sections rendered between Connection and About.
   * Each gets its own SectionHeader + divider; the host owns the
   * body content (e.g. headless `DndEditor`). Pass undefined or []
   * to render nothing.
   */
  extraSections?: SettingsPanelExtraSection[];

  // ── About — version always shown when provided ───────────────────
  version?: string;
  /**
   * Email address rendered as a `mailto:` link in the support footer
   * (when `labels.needHelp` is also provided). The host is responsible
   * for passing a SAFE value — no `javascript:`, `data:`, or
   * untrusted-user-input URLs. React 16.9+ strips `javascript:` hrefs
   * defensively, but the contract here is that consumers hardcode (or
   * server-validate) the address; this component performs no
   * additional sanitisation.
   */
  supportEmail?: string;

  /** Optional — opens the host's feedback dialog. Renders a "Send feedback" entry when `labels.sendFeedback` is also set. */
  onSendFeedback?: () => void;

  onBack(): void;
  labels: SettingsPanelLabels;
}

export function SettingsPanel({
  theme,
  language,
  onThemeChange,
  osNotifications,
  callNotifications,
  privacy,
  connection,
  extraSections,
  version,
  supportEmail,
  onSendFeedback,
  onBack,
  labels,
}: SettingsPanelProps) {
  const [confirming, setConfirming] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);
  // Stable ids so the visible Text label and the Toggle's internal
  // <input> share an `htmlFor`/`id` pair — clicking the label text
  // activates the switch for sighted users.
  const osToggleId = useId();
  const callToggleId = useId();
  const analyticsToggleId = useId();
  const themeLabelId = useId();
  const languageSelectId = useId();

  const handleDisconnect = useCallback(async () => {
    if (!connection) return;
    setDisconnecting(true);
    try {
      await connection.onDisconnect();
    } catch {
      // Reset the confirm dialog on error so the user lands back on
      // the primary disconnect button instead of a stale confirm
      // row with re-enabled Yes/Cancel. Errors themselves are the
      // host's responsibility to log/surface (this package stays
      // logging-free per CLAUDE.md).
      setDisconnecting(false);
      setConfirming(false);
      return;
    }
    setDisconnecting(false);
    setConfirming(false);
    // Narrow the dep to the function ref rather than the whole
    // `connection` prop. Both host wrappers pass `connection` as an
    // inline object literal that gets a fresh reference on every
    // render (and `paired` / `connected` re-render on every SSE
    // status toggle via `useStorage`). Depending on the whole
    // object would defeat the memoization on the SSE-flap hot path;
    // depending on the function ref keeps it stable across status
    // changes — host wrappers should still keep `onDisconnect` itself
    // stable via `useCallback`.
  }, [connection?.onDisconnect, connection]);

  const showNotificationsSection = osNotifications !== undefined || callNotifications !== undefined;
  const showAboutSection = version !== undefined || supportEmail !== undefined;

  // Message carries the shared __EMAIL__ sentinel (utils/emailSentinel.ts);
  // split to inject the <Link> at the placeholder position.
  let supportFooter: ReactNode = null;
  if (supportEmail && labels.needHelp) {
    const { before: needHelpBefore, after: needHelpAfter } = splitOnEmailSentinel(labels.needHelp);
    supportFooter = (
      <Text as="div" size="s" tone="tertiary" className={styles.supportRow}>
        {needHelpBefore}
        <Link href={`mailto:${supportEmail}`}>{supportEmail}</Link>
        {needHelpAfter}
      </Text>
    );
  }

  return (
    <div className={styles.container}>
      <header className={styles.header}>
        <IconButton
          icon={ChevronLeft}
          label={labels.back}
          variant="default"
          size="m"
          onClick={onBack}
        />
        <Text as="h2" size="m" weight="semibold" className={styles.title}>
          {labels.settings}
        </Text>
      </header>

      <div className={styles.content}>
        {/* ─── Appearance ─────────────────────────────────────── */}
        <section className={styles.section}>
          <SectionHeader>{labels.appearance}</SectionHeader>
          <div className={styles.row}>
            <Text id={themeLabelId} size="s" weight="medium">
              {labels.theme}
            </Text>
            {/* Icon-only TabGroup per Figma spec — contrast / sun / moon
             *  map to Auto / Light / Dark. aria-labelledby points at the
             *  visible "Theme" label so screen readers read the same
             *  string; per-tab aria-labels announce the theme names. */}
            <TabGroup
              value={theme}
              // TabGroup's `onChange` returns the raw `value` string —
              // narrow back to the `ThemeValue` union since we control
              // the Tab values just below (auto / light / dark).
              onChange={(v) => onThemeChange(v as ThemeValue)}
              aria-labelledby={themeLabelId}
            >
              <Tab value="auto" icon={Contrast01} aria-label={labels.themeAuto} />
              <Tab value="light" icon={Sun} aria-label={labels.themeLight} />
              <Tab value="dark" icon={Moon01} aria-label={labels.themeDark} />
            </TabGroup>
          </div>
          {language && (
            <div className={styles.row}>
              <label className={styles.rowLabel} htmlFor={languageSelectId}>
                <Text size="s" weight="medium">
                  {language.labels.language}
                </Text>
              </label>
              {/* Native <select>: 20+ options rule out TabGroup and
               *  client-ui ships no Select primitive. The empty-string
               *  value encodes "System default" (null override) since
               *  <option> values must be strings. */}
              <select
                id={languageSelectId}
                className={styles.languageSelect}
                value={language.value ?? ""}
                onChange={(e) => language.onChange(e.target.value === "" ? null : e.target.value)}
              >
                <option value="">{language.labels.systemDefault}</option>
                {language.options.map((o) => (
                  <option key={o.tag} value={o.tag}>
                    {o.endonym}
                  </option>
                ))}
              </select>
            </div>
          )}
        </section>

        {showNotificationsSection && (
          <>
            <SectionDivider />

            {/* ─── Notifications ──────────────────────────────────── */}
            <section className={styles.section}>
              <SectionHeader>{labels.notificationsSection}</SectionHeader>
              {osNotifications && (
                <div className={styles.row}>
                  {/* Sibling <label htmlFor> points at the same input id
                   *  the Toggle uses internally — clicking the visible
                   *  text activates the switch. (Outer label wrapping the
                   *  row would nest inside Toggle's own <label>; using
                   *  htmlFor sidesteps the invalid-HTML problem.)
                   *
                   *  Two `<label>` elements end up associated with the
                   *  same input id: this sibling one (visible text) +
                   *  the Toggle's internal `<label>` wrapper (track +
                   *  thumb). Both targets activate the input via native
                   *  click-to-toggle, but the input's accessible NAME
                   *  comes from `aria-label` alone (per ARIA precedence:
                   *  aria-labelledby > aria-label > <label> association),
                   *  so AT announces the toggle once — not twice. */}
                  <label className={styles.rowLabel} htmlFor={osToggleId}>
                    <Text size="s" weight="medium">
                      {labels.osNotifications}
                    </Text>
                  </label>
                  <Toggle
                    id={osToggleId}
                    size="lg"
                    checked={osNotifications.value}
                    onChange={osNotifications.onChange}
                    aria-label={labels.osNotifications}
                  />
                </div>
              )}
              {callNotifications && (
                <div className={styles.row}>
                  <label className={styles.rowLabel} htmlFor={callToggleId}>
                    <Text size="s" weight="medium">
                      {labels.callNotifications}
                    </Text>
                  </label>
                  <Toggle
                    id={callToggleId}
                    size="lg"
                    checked={callNotifications.value}
                    onChange={callNotifications.onChange}
                    aria-label={labels.callNotifications}
                  />
                </div>
              )}
            </section>
          </>
        )}

        {privacy && (
          <>
            <SectionDivider />

            {/* ─── Privacy ────────────────────────────────────────── */}
            <section className={styles.section}>
              <SectionHeader>{privacy.labels.section}</SectionHeader>
              <div className={styles.row}>
                <label className={styles.rowLabel} htmlFor={analyticsToggleId}>
                  <Text size="s" weight="medium">
                    {privacy.labels.analyticsToggle}
                  </Text>
                </label>
                <Toggle
                  id={analyticsToggleId}
                  size="lg"
                  checked={privacy.analyticsEnabled.value}
                  onChange={privacy.analyticsEnabled.onChange}
                  aria-label={privacy.labels.analyticsToggle}
                />
              </div>
              <Text as="p" size="xs" tone="tertiary" className={styles.rowDescription}>
                {privacy.labels.analyticsToggleDescription}
              </Text>
            </section>
          </>
        )}

        {connection && (
          <>
            <SectionDivider />

            {/* ─── Connection ─────────────────────────────────────── */}
            <section className={styles.section}>
              <SectionHeader>{labels.connection}</SectionHeader>
              <div className={styles.row}>
                <Text size="s" weight="medium">
                  {labels.status}
                </Text>
                {connection.connected ? (
                  <Chip tone="success" leadingIcon={Check}>
                    {labels.statusConnected}
                  </Chip>
                ) : (
                  // Figma only draws the Connected state; for reconnecting
                  // / disconnected we reuse Chip with tone="negative" +
                  // no icon so the control stays consistent in shape but
                  // communicates the error state via color + text.
                  <Chip tone="negative">
                    {connection.paired ? labels.statusReconnecting : labels.statusDisconnected}
                  </Chip>
                )}
              </div>
              {connection.paired && (
                <div className={styles.disconnectArea}>
                  {!confirming ? (
                    <Button
                      variant="negative"
                      size="m"
                      fullWidth
                      leadingIcon={LogOut04}
                      onClick={() => setConfirming(true)}
                      disabled={disconnecting}
                    >
                      {labels.disconnectFromPhone}
                    </Button>
                  ) : (
                    // Two-step confirm flow retained per product requirement
                    // (safety net against accidental taps) even though the
                    // Figma spec only shows the bare button.
                    <div className={styles.confirmRow}>
                      <Text size="s" tone="secondary">
                        {labels.disconnectConfirm}
                      </Text>
                      <div className={styles.confirmActions}>
                        <Button
                          variant="negative"
                          size="s"
                          className={styles.confirmAction}
                          loading={disconnecting}
                          onClick={handleDisconnect}
                        >
                          {labels.yesDisconnect}
                        </Button>
                        <Button
                          variant="secondary"
                          size="s"
                          className={styles.confirmAction}
                          disabled={disconnecting}
                          onClick={() => setConfirming(false)}
                        >
                          {labels.cancel}
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </section>
          </>
        )}

        {extraSections?.map((section) => (
          <div key={section.id}>
            <SectionDivider />
            <section className={styles.section}>
              {section.title && <SectionHeader>{section.title}</SectionHeader>}
              {section.content}
            </section>
          </div>
        ))}

        {onSendFeedback && labels.sendFeedback && (
          <>
            <SectionDivider />
            <section className={styles.section}>
              <div className={styles.row}>
                <Button variant="secondary" size="s" onClick={onSendFeedback}>
                  {labels.sendFeedback}
                </Button>
              </div>
            </section>
          </>
        )}

        {showAboutSection && (
          <>
            <SectionDivider />

            {/* ─── About & Support ────────────────────────────────── */}
            {/* Pared back per the refreshed Settings design: only version,
             *  encryption, and support email remain. Extension ID and
             *  phone ID rows (with copy buttons) were removed — they're
             *  debugging affordances that rarely surface in user flows
             *  and the design prioritizes a cleaner, shorter Settings. */}
            <section className={styles.section}>
              <SectionHeader>{labels.aboutAndSupport}</SectionHeader>
              {version !== undefined && (
                <div className={styles.row}>
                  <Text size="s" weight="medium">
                    {labels.version}
                  </Text>
                  <Text size="s" tone="tertiary">
                    {version}
                  </Text>
                </div>
              )}
              <div className={styles.row}>
                <Text size="s" weight="medium">
                  {labels.encryption}
                </Text>
                <Text size="s" tone="tertiary">
                  {labels.encryptionValue}
                </Text>
              </div>
              {supportFooter}
            </section>
          </>
        )}
      </div>
    </div>
  );
}

function SectionHeader({ children }: { children: React.ReactNode }) {
  return (
    <Text as="h3" size="s" weight="semibold" tone="tertiary" className={styles.sectionHeader}>
      {children}
    </Text>
  );
}

function SectionDivider() {
  return <div className={styles.sectionDivider} aria-hidden="true" />;
}
